import React, { useState } from 'react';
import Card from '../../../ui/Card/Card';
import Button from '../../../ui/Button/Button';
import IconButton from '../../../ui/IconButton';
import Icon from '../../../ui/Icon';
import { useNavigate } from 'react-router-dom';
import Badge from '../../../ui/Badge/Badge';
import EmptyState from '../../../ui/EmptyState';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { Input } from '../../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellText, CellId, CellMoney, CellDate, CellTag } from '../../../ui/DataTable/cells';
import { ICONS } from '../../../ui/icon-map';
import { useModal } from '../../../contexts/ModalContext';
import { custodyService } from '../../../services/custodyService';
import { usePermissions } from '../../../hooks/usePermissions';
import CustodyStatusBadge from './CustodyStatusBadge';
import { custodyActionsFor, custodyErrorText } from '../custodyRules';

// Custom rows (a custody row + its matched-invoice sub-table shown while searching),
// so this list uses the low-level table parts rather than DataTable.
const CustodyList = ({ custodies, loading, onRefresh }) => {
    const navigate = useNavigate();
    const { showConfirm, showToast } = useModal();
    const { hasPermission } = usePermissions();
    // delete needs delete_custody AND the write the database rules ask for (ADM-18)
    const canDelete = custodyActionsFor(hasPermission).remove;
    const [searchTerm, setSearchTerm] = useState('');
    const [matchingCustodyIds, setMatchingCustodyIds] = useState([]);
    const [matchingExpenses, setMatchingExpenses] = useState({}); // Map: custodyId -> [expenses]
    const [isSearching, setIsSearching] = useState(false);

    // Debounced Search for Invoices
    React.useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            if (searchTerm.trim().length > 2) {
                // Only search server if term looks like potential invoice (e.g. > 2 chars)
                setIsSearching(true);
                const { ids, expenses } = await custodyService.findCustodyIdsByInvoice(searchTerm.trim());
                setMatchingCustodyIds(ids);

                // Group expenses by custody_id
                const expMap = {};
                expenses.forEach(exp => {
                    if (!expMap[exp.custody_id]) expMap[exp.custody_id] = [];
                    expMap[exp.custody_id].push(exp);
                });
                setMatchingExpenses(expMap);

                setIsSearching(false);
            } else {
                setMatchingCustodyIds([]);
                setMatchingExpenses({});
            }
        }, 800);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const filtered = custodies.filter(c => {
        const matchesName = c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (c.cost_center && c.cost_center.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesInvoice = matchingCustodyIds.includes(c.id);

        return matchesName || matchesInvoice;
    });

    const handleDelete = (custody, e) => {
        e.stopPropagation();
        showConfirm({
            title: 'Delete Custody',
            message: `Are you sure you want to PERMANENTLY delete custody "${custody.name}"? This cannot be undone.`,
            confirmLabel: 'Delete',
            isDangerous: true,
            onConfirm: async () => {
                // deleteCustody reports a refused delete as { success: false }: say it (ADM-18)
                const res = await custodyService.deleteCustody(custody.id);
                if (!res || res.success === false) {
                    showToast(custodyErrorText(res, 'Deleting the custody'), 'error');
                    return;
                }
                onRefresh();
            }
        });
    };

    if (loading) return <LoadingSpinner message="Loading custodies..." />;

    return (
        <Card padding="none">
            <div className="ds-toolbar p-3 border-b border-gray-200">
                <div className="w-full sm:w-72">
                    <Input
                        type="search"
                        size="sm"
                        aria-label="Search custodies by name, cost center or invoice number"
                        prefix={<Icon glyph={ICONS.search} size="sm" />}
                        suffix={isSearching ? <LoadingSpinner size="sm" message="Searching invoices" /> : undefined}
                        placeholder="Search custodies..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <span className="ds-toolbar__spacer" />
                <Button variant="ghost" size="sm" onClick={onRefresh} leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}>
                    Refresh
                </Button>
            </div>

            <TableContainer className="border-0 rounded-none overflow-x-auto">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeader>Custody Name</TableHeader>
                            <TableHeader>Open Date</TableHeader>
                            <TableHeader>Total Amount</TableHeader>
                            <TableHeader>Spent</TableHeader>
                            <TableHeader>Remaining</TableHeader>
                            <TableHeader>Status</TableHeader>
                            <TableHeader>Action</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7}>
                                    <EmptyState icon={ICONS.wallet} title="No custodies found." />
                                </TableCell>
                            </TableRow>
                        ) : (
                            filtered.map((custody) => (
                                <React.Fragment key={custody.id}>
                                    <TableRow>
                                        <TableCell>
                                            <div className="flex items-center justify-center gap-3">
                                                <span className="flex items-center justify-center w-8 h-8 rounded-md bg-gray-100 text-gray-600 shrink-0">
                                                    <Icon glyph={ICONS.wallet} size="sm" />
                                                </span>
                                                <div className="flex flex-col items-center">
                                                    <CellText>{custody.name}</CellText>
                                                    {matchingCustodyIds.includes(custody.id) && (
                                                        <Badge variant="accent" size="sm">
                                                            <Icon glyph={ICONS.document} size="xs" /> Contains Invoice Match
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap"><span className="ds-cell-date">{new Date(custody.open_date).toLocaleDateString('en-GB')}</span></TableCell>
                                        <TableCell className="whitespace-nowrap"><CellMoney value={Number(custody.total_amount)} currency="AED" /></TableCell>
                                        <TableCell className="whitespace-nowrap"><CellMoney value={Number(custody.spent || 0)} currency="AED" tone="danger" /></TableCell>
                                        <TableCell className="whitespace-nowrap"><CellMoney value={Number(custody.remaining)} currency="AED" tone={Number(custody.remaining) < 0 ? 'danger' : 'success'} /></TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            <CustodyStatusBadge status={custody.status} remaining={custody.remaining} />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex justify-center gap-1">
                                                <Button
                                                    size="sm"
                                                    onClick={() => navigate(`/custodies/${custody.id}`)}
                                                >
                                                    View Details
                                                </Button>
                                                {canDelete && (
                                                    <IconButton
                                                        icon={ICONS.delete}
                                                        label={`Delete custody ${custody.name}`}
                                                        size="sm"
                                                        tone="danger"
                                                        onClick={(e) => handleDelete(custody, e)}
                                                    />
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    {/* Render Matching Expenses Row */}
                                    {matchingCustodyIds.includes(custody.id) && matchingExpenses[custody.id] && (
                                        <TableRow className="bg-gray-50">
                                            <TableCell colSpan={7}>
                                                <div className="ml-11 flex flex-col gap-2">
                                                    <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                                                        <Icon glyph={ICONS.search} size="xs" /> Matched invoices found in this custody
                                                    </h4>
                                                    <TableContainer>
                                                        <Table>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableHeader>Date</TableHeader>
                                                                    <TableHeader>Category</TableHeader>
                                                                    <TableHeader>Description</TableHeader>
                                                                    <TableHeader>Invoice No.</TableHeader>
                                                                    <TableHeader>Amount</TableHeader>
                                                                    <TableHeader>Receipt</TableHeader>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {matchingExpenses[custody.id].map(exp => (
                                                                    <TableRow key={exp.id}>
                                                                        <TableCell className="whitespace-nowrap"><CellDate value={exp.expense_date} /></TableCell>
                                                                        <TableCell><CellTag>{exp.category}</CellTag></TableCell>
                                                                        <TableCell><CellText>{exp.description}</CellText></TableCell>
                                                                        <TableCell><CellId>{exp.invoice_number}</CellId></TableCell>
                                                                        <TableCell className="whitespace-nowrap"><CellMoney value={Number(exp.amount)} currency="AED" tone="danger" /></TableCell>
                                                                        <TableCell>
                                                                            {exp.attachment_url ? (
                                                                                <a
                                                                                    href={exp.attachment_url}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="inline-flex items-center gap-1 text-purple-700 hover:underline"
                                                                                    aria-label={`Open receipt for invoice ${exp.invoice_number || ''}`}
                                                                                >
                                                                                    <Icon glyph={ICONS.download} size="sm" />
                                                                                </a>
                                                                            ) : (
                                                                                <span className="ds-cell-empty">—</span>
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );
};

export default CustodyList;
